import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Users, Eye, TrendingUp } from 'lucide-react';

type PeriodType = 'YESTERDAY' | 'TODAY' | 'DAILY' | 'WEEKLY' | 'MONTHLY';

/** 어제 날짜 (YYYY-MM-DD) */
const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

/** 터치포인트 축약 라벨 */
const LOCATION_SHORT_LABELS: Record<string, string> = {
  VOTE_AD_BONUS: '투표',
  DAILY_AD_BONUS: '일일',
  ATTENDANCE_AD_BONUS: '출석',
  ATTENDANCE_STREAK_SAVE: '연속출석',
  MILESTONE_AD_BONUS: '마일스톤',
  CREATOR_MILESTONE_AD_BONUS: '창작마일스톤',
  ANALYSIS_UNLOCK: '분석',
  EXCHANGE: '꺼내기',
  COMMENT_AD_BONUS: '댓글',
  SURVEY_LIKE_AD_BONUS: '좋아요',
  COMMENT_LIKE_AD_BONUS: '댓글좋아요',
  WEEKLY_MISSION_AD_BONUS: '미션',
};

const getLocationLabel = (key: string) => LOCATION_SHORT_LABELS[key] || key;

/** 오늘 날짜 (YYYY-MM-DD) */
const today = () => new Date().toISOString().slice(0, 10);

/** 이번 주 월요일 (YYYY-MM-DD) */
const thisMonday = () => {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().slice(0, 10);
};

/** 이번 달 (YYYY-MM) */
const thisMonth = () => new Date().toISOString().slice(0, 7);

export const AdEcpmManagement = () => {
  const [periodType, setPeriodType] = useState<PeriodType>('TODAY');
  const [dateValue, setDateValue] = useState(today());
  const [weekValue, setWeekValue] = useState(thisMonday());
  const [monthValue, setMonthValue] = useState(thisMonth());

  // YESTERDAY는 FE 전용 — BE에는 DAILY + 어제 날짜로 전송
  const { apiPeriodType, queryDate } = useMemo(() => {
    switch (periodType) {
      case 'YESTERDAY': return { apiPeriodType: 'DAILY', queryDate: yesterday() };
      case 'TODAY': return { apiPeriodType: 'TODAY', queryDate: undefined };
      case 'DAILY': return { apiPeriodType: 'DAILY', queryDate: dateValue };
      case 'WEEKLY': return { apiPeriodType: 'WEEKLY', queryDate: weekValue };
      case 'MONTHLY': return { apiPeriodType: 'MONTHLY', queryDate: monthValue };
    }
  }, [periodType, dateValue, weekValue, monthValue]);

  const { data, isLoading } = useQuery({
    queryKey: ['ecpmUserStats', apiPeriodType, queryDate],
    queryFn: () => adApi.getEcpmUserStats(apiPeriodType, queryDate).then(res => res.data),
  });

  // 모든 유저의 locationBreakdown에서 등장하는 위치 키 수집 (전체 0이면 제외)
  const locationColumns = useMemo(() => {
    if (!data?.users?.length) return [];
    const sums: Record<string, number> = {};
    data.users.forEach(u => {
      Object.entries(u.locationBreakdown).forEach(([key, val]) => {
        sums[key] = (sums[key] || 0) + val;
      });
    });
    return Object.entries(sums)
      .filter(([, total]) => total > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([key]) => key);
  }, [data]);

  const handlePeriodChange = (type: PeriodType) => {
    setPeriodType(type);
  };

  return (
    <div className="space-y-4">
      {/* 기간 선택 */}
      <div className="flex flex-wrap items-center gap-2">
        {(['YESTERDAY', 'TODAY', 'DAILY', 'WEEKLY', 'MONTHLY'] as PeriodType[]).map(type => (
          <Button
            key={type}
            variant={periodType === type ? 'default' : 'outline'}
            size="sm"
            onClick={() => handlePeriodChange(type)}
          >
            {type === 'YESTERDAY' ? '어제' : type === 'TODAY' ? '오늘' : type === 'DAILY' ? '일별' : type === 'WEEKLY' ? '주별' : '월별'}
          </Button>
        ))}

        {periodType === 'DAILY' && (
          <input
            type="date"
            className="border rounded-md px-3 py-1.5 text-sm"
            value={dateValue}
            onChange={e => setDateValue(e.target.value)}
          />
        )}
        {periodType === 'WEEKLY' && (
          <input
            type="date"
            className="border rounded-md px-3 py-1.5 text-sm"
            value={weekValue}
            onChange={e => setWeekValue(e.target.value)}
          />
        )}
        {periodType === 'MONTHLY' && (
          <input
            type="month"
            className="border rounded-md px-3 py-1.5 text-sm"
            value={monthValue}
            onChange={e => setMonthValue(e.target.value)}
          />
        )}

        {data?.periodLabel && (
          <span className="text-sm text-gray-500 ml-2">{data.periodLabel}</span>
        )}
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="h-full flex items-center">
          <div className="p-4 w-full flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">시청 유저 / 활성 유저</p>
              <p className="text-lg font-bold text-gray-900">{data?.totalUsers?.toLocaleString() ?? '-'} / {data?.activeUsers?.toLocaleString() ?? '-'}명</p>
            </div>
          </div>
        </Card>
        <Card className="h-full flex items-center">
          <div className="p-4 w-full flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-50">
              <Eye className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">총 시청</p>
              <p className="text-lg font-bold text-gray-900">{data?.totalWatchCount?.toLocaleString() ?? '-'}회</p>
            </div>
          </div>
        </Card>
        <Card className="h-full flex items-center">
          <div className="p-4 w-full flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-50">
              <TrendingUp className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">1인당 평균 (시청유저)</p>
              <p className="text-lg font-bold text-gray-900">{data?.avgWatchPerUser ?? '-'}회</p>
            </div>
          </div>
        </Card>
        <Card className="h-full flex items-center">
          <div className="p-4 w-full flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-50">
              <TrendingUp className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">1인당 평균 (활성유저)</p>
              <p className="text-lg font-bold text-gray-900">{data?.avgWatchPerActiveUser ?? '-'}회</p>
            </div>
          </div>
        </Card>
      </div>

      {/* 유저별 테이블 */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs whitespace-nowrap">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">유저</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">총시청</th>
                  {locationColumns.map(loc => (
                    <th key={loc} className="px-3 py-2 text-right font-medium text-gray-600">
                      {getLocationLabel(loc)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading ? (
                  <tr>
                    <td colSpan={3 + locationColumns.length} className="px-3 py-8 text-center text-gray-500">
                      로딩 중...
                    </td>
                  </tr>
                ) : !data?.users?.length ? (
                  <tr>
                    <td colSpan={3 + locationColumns.length} className="px-3 py-8 text-center text-gray-500">
                      데이터가 없습니다.
                    </td>
                  </tr>
                ) : (
                  data.users.map((user, idx) => {
                    const isCherryPicker = user.totalWatch <= 2;
                    return (
                      <tr
                        key={user.userId}
                        className={isCherryPicker ? 'bg-red-50' : 'hover:bg-gray-50'}
                      >
                        <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                        <td className="px-3 py-2">
                          <span className="font-medium">{user.nickname}</span>
                          <span className="text-gray-400 ml-1">({user.userId})</span>
                        </td>
                        <td className="px-3 py-2 text-right font-bold">{user.totalWatch}</td>
                        {locationColumns.map(loc => (
                          <td key={loc} className="px-3 py-2 text-right text-gray-600">
                            {user.locationBreakdown[loc] || 0}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
